import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {TermwebConfiguration} from '@twpub/core/models';
import {ClientConfigurationService, DictionaryService} from '@twpub/core/services';
import {TenantService} from '@twpub/core/services/tenant.service';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';

import {
  TermwebConnectionSettingsFormComponent
} from '../termweb-connection-settings-form/termweb-connection-settings-form.component';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import {TermwebStatusTextComponent} from '../termweb-status-text/termweb-status-text.component';
import {
  TermwebConnectionSettingsDisplayComponent
} from '../termweb-connection-settings-display/termweb-connection-settings-display.component';
import {Observable, of} from 'rxjs';

@Component({
  selector: 'pub-termweb-connection-settings',
  templateUrl: './termweb-connection-settings.component.html',
  styleUrls: ['./termweb-connection-settings.component.scss'],
  standalone: true,
  imports: [MatCard, MatCardHeader, MatCardTitle, MatCardContent, TermwebConnectionSettingsFormComponent, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, TermwebStatusTextComponent, TermwebConnectionSettingsDisplayComponent]
})
export class TermwebConnectionSettingsComponent implements OnInit {
  /** When set, operates on a specific tenant (superadmin mode) instead of the current tenant. */
  @Input() tenantId?: number;
  /** Controls whether the edit button is shown. Defaults to true. */
  @Input() editable = true;

  @ViewChild(TermwebConnectionSettingsFormComponent) private settingsForm?: TermwebConnectionSettingsFormComponent;

  termwebConfigurations: TermwebConfiguration[] = [];
  editConfiguration?: TermwebConfiguration;
  currentConfiguration?: TermwebConfiguration;
  lockUrlClient = false;

  constructor(
    private configService: ClientConfigurationService,
    private tenantService: TenantService,
    private dictionaryService: DictionaryService
  ) {
  }

  ngOnInit(): void {
    this.fetchConfigurations();
    this.refreshLockState();
  }

  private refreshLockState(): void {
    this.dictionaryService.getDictionaryStatistics().subscribe({
      next: (stats) => {
        this.lockUrlClient = (stats || []).some(s =>
          s.publishedConceptCount > 0
          && !s.deleted
          && s.syncStats?.status !== 'FAILED');
      },
      error: () => {
        this.lockUrlClient = false;
      }
    });
  }

  private fetchConfigurations() {
    this.getConfigs().subscribe({
      next: (configs) => {
        this.termwebConfigurations = configs;
        this.currentConfiguration = configs?.[0];
        if (!this.currentConfiguration) {
          this.editConfiguration = this.initNewConfiguration();
        }
      }
    });
  }

  private getConfigs(): Observable<TermwebConfiguration[]> {
    if (this.tenantId) {
      return this.tenantService.getTermWebConfig(this.tenantId);
    }
    return this.configService.getTermWebConfigurations();
  }

  private saveConfig(config: TermwebConfiguration): Observable<any> {
    if (this.tenantId) {
      return this.tenantService.saveTermWebConfig(this.tenantId, config);
    }
    return this.configService.saveTermWebConfiguration(config);
  }

  private initNewConfiguration(): TermwebConfiguration {
    return {id: this.tenantId || 1, url: '', client: '', userName: '', password: ''} as TermwebConfiguration;
  }

  saveConfiguration(configuration: TermwebConfiguration | undefined) {
    if (configuration) {
      this.saveConfig(configuration).subscribe({
        next: () => {
          this.getConfigs().subscribe({
            next: (configs) => {
              this.termwebConfigurations = configs;
              this.currentConfiguration = configs?.[0];
            }
          });
          this.refreshLockState();
        }
      });
    }
    this.editConfiguration = undefined;
  }

  /** Triggers save on the embedded form (used when buttons are hidden). Returns Observable<boolean> indicating success. */
  triggerSave(): Observable<boolean> {
    if (this.settingsForm) {
      return this.settingsForm.saveAndReport();
    }
    return of(true);
  }

  setEditConfig(config: TermwebConfiguration) {
    this.editConfiguration = config;
  }
}
